import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Music, Guitar, Search, Headphones } from 'lucide-react';

export const LoginPage = () => {
  const { login, user, loading, initialized } = useAuth();
  const navigate = useNavigate();

  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (initialized && !loading && user) {
      console.log('LoginPage: User already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, initialized, navigate]);

  // Show loading while checking auth
  if (loading || !initialized) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <div className="noise-overlay"></div>
        <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] flex flex-col relative overflow-hidden">
      {/* Noise overlay */}
      <div className="noise-overlay"></div>
      
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-ss-cyan/[0.04] rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-ss-purple/[0.05] rounded-full blur-[100px]"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-ss-red/[0.02] rounded-full blur-[150px]"></div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
        <div className="max-w-md w-full animate-slide-up">
          {/* Login Card */}
          <div className="bg-[rgba(20,20,25,0.85)] backdrop-blur-xl rounded-2xl border border-white/[0.08] shadow-2xl p-8 md:p-10">
            
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-ss-surface to-ss-bg border border-ss-cyan/30 flex items-center justify-center shadow-lg shadow-ss-cyan/10">
                <Music className="w-8 h-8 text-ss-cyan" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight uppercase text-white mb-1">
                Sacred Steel
              </h1>
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-ss-cyan mb-3">
                Explorer
              </h2>
              <p className="text-xs text-ss-muted tracking-[0.2em] uppercase">
                By StreakLabs
              </p>
            </div>

            {/* Tagline */}
            <p className="text-center text-ss-muted text-sm mb-8 leading-relaxed">
              Find chords. Explore tunings. Master the fretboard.
            </p>

            {/* Google Login Button */}
            <Button
              onClick={login}
              data-testid="google-login-btn"
              className="w-full h-12 bg-white hover:bg-gray-50 text-gray-800 font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 mb-4"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            {/* Privacy text */}
            <p className="text-center text-[11px] text-ss-muted/70">
              Secure sign-in powered by Google OAuth
            </p>
          </div>

          {/* Feature Preview - Below card */}
          <div className="mt-8 grid grid-cols-3 gap-3">
            <div className="bg-[rgba(20,20,25,0.6)] backdrop-blur-sm rounded-xl p-4 border border-white/[0.05] text-center">
              <Guitar className="w-6 h-6 text-ss-cyan mx-auto mb-2" />
              <p className="text-[11px] text-ss-muted leading-tight">
                Pedal Steel &<br/>Lap Steel Tunings
              </p>
            </div>
            <div className="bg-[rgba(20,20,25,0.6)] backdrop-blur-sm rounded-xl p-4 border border-white/[0.05] text-center">
              <Search className="w-6 h-6 text-ss-red mx-auto mb-2" />
              <p className="text-[11px] text-ss-muted leading-tight">
                Chord Finder<br/>Engine
              </p>
            </div>
            <div className="bg-[rgba(20,20,25,0.6)] backdrop-blur-sm rounded-xl p-4 border border-white/[0.05] text-center">
              <Headphones className="w-6 h-6 text-ss-purple mx-auto mb-2" />
              <p className="text-[11px] text-ss-muted leading-tight">
                Practice<br/>Shed Tracks
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center text-ss-muted/50 text-xs relative z-10">
        © 2026 StreakLabs. All rights reserved.
      </footer>
    </div>
  );
};

export default LoginPage;
